import { useState } from 'react';
import { relationName } from '@/lib/relations';
import { useNavigate } from 'react-router-dom';
import { FileJson } from 'lucide-react';
import { downloadPlotsGeoJson } from '@/lib/geojsonExport';
import { formatArea } from '@/lib/area';
import { placeLine } from '@/lib/address';
import { toast } from '@/lib/toast';
import { Drawer, QueryState, RecordTable, StatusPill, formatDate, formatNumber, type Column } from '@/components/live';
import { StatusChanger } from '@/components/StatusChanger';
import { ApprovalPanel } from '@/components/RecordDrawer';
import { nameMatchLabel } from '@/lib/nameMatch';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { FarmerKycSection } from '@/screens/parts/FarmerKycSection';
import { FarmerProfile } from '@/screens/parts/FarmerProfile';
import { RecordHistory } from '@/components/RecordHistory';
import { RepresentativesTab } from '@/screens/parts/RepresentativesTab';
import { StakeholderList } from '@/screens/StakeholdersScreen';
import { ConsentPosition } from '@/screens/parts/ConsentPosition';
import { consentTypeLabel } from '@/lib/consentTypes';
import { languageName } from '@/lib/languages';
import { FarmerPhoto, usePhotoUrls } from '@/components/FarmerPhoto';
import type { ConsentRow, FarmerRow, PartyRow, PlotRow } from '@/types/records';
import { PlotConsentDrawer, PlotConsentList } from '@/screens/parts/PlotConsent';

type Tab = 'summary' | 'kyc' | 'plots' | 'family' | 'stakeholders' | 'consent' | 'approval' | 'history';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'summary', label: 'Summary' },
  { id: 'kyc', label: 'KYC' },
  { id: 'plots', label: 'Plots' },
  { id: 'family', label: 'Family & representatives' },
  { id: 'stakeholders', label: 'Stakeholders' },
  { id: 'consent', label: 'Consent' },
  { id: 'approval', label: 'Approval & lock' },
  { id: 'history', label: 'Change history' },
];

/**
 * Everything recorded about one farmer, read-only. Each tab is a separate query so opening the
 * drawer costs one round trip, not four - a field device on a weak connection pays for what
 * the user actually looks at.
 */
export function FarmerDetail({
  farmer,
  onClose,
  onEdit,
  canEdit,
}: { farmer: FarmerRow; onClose: () => void; onEdit: () => void; canEdit: boolean }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('summary');
  const [openConsent, setOpenConsent] = useState<ConsentRow | null>(null);
  const { profile } = useAuth();
  const referrerPhoto = usePhotoUrls([farmer.referred_by_photo_document_id]);

  const party = useRows<PartyRow>('parties', { filters: { id: farmer.party_id } }, tab === 'summary');
  const plots = useRows<PlotRow>('farm_plots', { filters: { farmer_id: farmer.id } }, tab === 'plots');
  const consents = useRows<ConsentRow>('party_consents', { filters: { party_id: farmer.party_id } }, tab === 'consent');

  const contact = party.data?.[0];

  const plotColumns: Array<Column<PlotRow>> = [
    { key: 'plot_no', label: 'Plot no.', render: (row) => <strong>{row.plot_no ?? '--'}</strong> },
    { key: 'plot_code', label: 'Plot code' },
    { key: 'plot_name', label: 'Name', render: (row) => row.plot_name ?? '--' },
    { key: 'declared_area_ha', label: 'Declared', align: 'right', render: (row) => formatArea(row.declared_area_ha) },
    { key: 'mapped_area_ha', label: 'Mapped', align: 'right', render: (row) => formatArea(row.mapped_area_ha) },
    { key: 'plot_status', label: 'Status', render: (row) => <StatusPill value={row.plot_status} /> },
  ];

  const consentColumns: Array<Column<ConsentRow>> = [
    { key: 'consent_type', label: 'Consent', render: (row) => consentTypeLabel(row.consent_type) },
    { key: 'language_code', label: 'Language', render: (row) => (row.language_code
      ? <span title={languageName(row.language_code)}>{row.language_code.toUpperCase()}</span> : '--') },
    { key: 'acknowledgement_method', label: 'Method', render: (row) => row.acknowledgement_method?.replace(/_/g, ' ') ?? '--' },
    { key: 'acknowledged_at', label: 'Acknowledged', render: (row) => formatDate(row.acknowledged_at) },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
  ];

  return (
    <Drawer
      footer={
        <>
          <DeleteRecordButton entity="farmer" id={farmer.id} invalidate={['farmers']}
                              label={`farmer ${farmer.farmer_code}`} onDeleted={onClose} screenKey="farmer.register" />
          <button onClick={onClose} type="button">Close</button>
          <button className="primary" disabled={!canEdit} onClick={onEdit}
                  title={canEdit ? undefined : 'Your role cannot edit farmers.'} type="button">
            Edit
          </button>
        </>
      }
      onClose={onClose}
      open
      subtitle={`${farmer.farmer_code} - ${farmer.village_name ?? 'no village'}`}
      title={farmer.full_name}
      wide
    >
      <div className="tab-row" role="tablist">
        {TABS.map((entry) => (
          <button aria-selected={tab === entry.id} className={tab === entry.id ? 'active' : ''}
                  key={entry.id} onClick={() => setTab(entry.id)} role="tab" type="button">
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'approval' && <ApprovalPanel record={{ table: 'people.farmers', id: farmer.id, invalidate: ['farmers'] }} />}
      {tab === 'summary' && <FarmerProfile farmer={farmer} />}

      {tab === 'summary' && (
        <StatusChanger current={farmer.status} entity="farmer" id={farmer.id}
                       invalidate={['farmers']} key={farmer.id + farmer.status} screenKey="farmer.register" />
      )}
      {tab === 'summary' && farmer.status_reason && (
        <p className="muted" style={{ margin: 0 }}>Reason on record: {farmer.status_reason}</p>
      )}

      {tab === 'summary' && (
        <dl className="detail-grid">
          <div><dt>Farmer code</dt><dd>{farmer.farmer_code}</dd></div>
          <div><dt>{farmer.member_type === 'entity' ? 'Name as per registration' : 'Name as per ID card'}</dt><dd>{farmer.name_as_per_id ?? '--'}
            {(farmer.name_match_basis || farmer.name_match_remarks) && (
              <small className="muted" style={{ display: 'block' }}>
                {[farmer.name_match_basis ? nameMatchLabel(farmer.name_match_basis) : null, farmer.name_match_remarks].filter(Boolean).join(': ')}
              </small>
            )}</dd></div>
          {farmer.is_draft && <div><dt>Entry</dt><dd><StatusPill value="draft" /></dd></div>}
          <div><dt>Member type</dt><dd>{farmer.member_type === 'entity' ? 'Entity' : 'Farmer'}</dd></div>
          {farmer.member_type === 'entity' && <div><dt>Entity type</dt><dd>{ENTITY_TYPE_LABEL[farmer.entity_type ?? ''] ?? farmer.entity_type ?? '--'}</dd></div>}
          {farmer.member_type === 'entity' && <div><dt>Registration no.</dt><dd>{farmer.registration_no ?? '--'}</dd></div>}
          {farmer.member_type === 'entity' && <div><dt>Contact person</dt><dd>{farmer.contact_person_name ?? '--'}</dd></div>}
          {farmer.member_type !== 'entity' && (
            <div><dt>Community / caste</dt><dd>{[
              ({ others: 'Others', obc: 'OBC', sc: 'SC', st: 'ST', ews: 'EWS', not_disclosed: 'Prefer not to say' } as Record<string, string>)[farmer.social_category ?? ''],
              farmer.caste,
            ].filter(Boolean).join(' · ') || '--'}</dd></div>
          )}
          {farmer.member_type !== 'entity' && (
            <div><dt>Indigenous (ST)</dt><dd>{farmer.is_indigenous == null ? 'Not recorded'
              : farmer.is_indigenous ? `Yes${farmer.indigenous_community ? ` - ${farmer.indigenous_community}` : ''}` : 'No'}</dd></div>
          )}
          <div><dt>Referred by</dt><dd>{farmer.referred_by_type ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {farmer.referred_by_photo_document_id && (
                <FarmerPhoto name={farmer.referred_by_name} size={32}
                             url={referrerPhoto.data?.[farmer.referred_by_photo_document_id]} />
              )}
              {`${farmer.referred_by_name ?? '--'} (${REFERRER_TYPE_LABEL[farmer.referred_by_type] ?? farmer.referred_by_type.replace(/_/g, ' ')})${farmer.referred_by_phone ? ` · ${farmer.referred_by_phone}` : ''}`}
            </span>
          ) : '--'}</dd></div>
          {farmer.member_type !== 'entity' && (
            <div><dt>Gender</dt><dd>{({ female: 'Female', male: 'Male', other: 'Other', not_disclosed: 'Prefer not to say' } as Record<string, string>)[farmer.gender ?? ''] ?? '--'}</dd></div>
          )}
          {farmer.member_type !== 'entity' && <div><dt>Father's or spouse's name</dt><dd>{relationName(farmer.relation_type, farmer.father_or_spouse_name) ?? '--'}</dd></div>}
          <div><dt>Village</dt><dd>{placeLine(farmer)}</dd></div>
          <div><dt>Communication address</dt><dd>{farmer.communication_address ?? '--'}</dd></div>
          <div><dt>PIN code</dt><dd>{farmer.pin_code ?? '--'}</dd></div>
          <div><dt>Status</dt><dd><StatusPill value={farmer.status} /></dd></div>
          <div><dt>Mobile</dt><dd>{contact?.primary_mobile ?? farmer.mobile ?? '--'}</dd></div>
          <div><dt>WhatsApp</dt><dd>{contact?.whatsapp_number ?? '--'}</dd></div>
          <div><dt>Email</dt><dd>{contact?.primary_email ?? '--'}</dd></div>
          <div><dt>Contact channels, in order</dt><dd>{channelsInOrder(farmer)}</dd></div>
          <div><dt>Languages, in order</dt><dd>{(farmer.language_order ?? [farmer.preferred_language]).map((code) => languageName(code)).join(' → ')}</dd></div>
          <div><dt>KYC</dt><dd><StatusPill value={farmer.kyc_status} /></dd></div>
          <div><dt>Consent</dt><dd><StatusPill value={farmer.consent_status} /></dd></div>
          <div><dt>Risk</dt><dd><StatusPill value={farmer.risk_status} /></dd></div>
          <div><dt>Plots</dt><dd>{formatNumber(farmer.plot_count)} ({formatNumber(farmer.mapped_plot_count)} mapped)</dd></div>
          <div><dt>Mapped area</dt><dd>{formatArea(farmer.total_mapped_area_ha)}</dd></div>
          <div><dt>Registered</dt><dd>{formatDate(farmer.created_at)}</dd></div>
        </dl>
      )}

      {tab === 'kyc' && <FarmerKycSection partyId={farmer.party_id} partyLabel={farmer.full_name} />}

      {tab === 'plots' && can(profile, 'plot.mapping', 'export') && (
        <div className="toolbar-row">
          <span className="muted">Every mapped boundary of this farmer, in one file.</span>
          <button className="icon-text" type="button"
                  disabled={!(plots.data ?? []).some((row) => row.is_mapped)}
                  onClick={() => {
                    const ids = (plots.data ?? []).filter((row) => row.is_mapped).map((row) => row.id);
                    downloadPlotsGeoJson(ids, `${farmer.farmer_code}-plots`)
                      .then((count) => (count ? toast.success(`Exported ${count} feature${count === 1 ? '' : 's'} as GeoJSON.`)
                                              : toast.error('No boundary could be exported.')))
                      .catch((error: unknown) => toast.error(error instanceof Error ? error.message : 'Export failed.'));
                  }}
                  title="Download this farmer's plot boundaries as GeoJSON">
            <FileJson size={16} /> GeoJSON
          </button>
        </div>
      )}
      {tab === 'plots' && (
        <QueryState empty="No plots linked yet. Add them on Plots (Farmer Groups, Onboarding)." query={plots}>
          <RecordTable columns={plotColumns} rows={plots.data ?? []} />
        </QueryState>
      )}

      {tab === 'family' && <RepresentativesTab farmer={farmer} />}

      {tab === 'stakeholders' && <StakeholderList farmerId={farmer.id} />}

      {tab === 'history' && <RecordHistory commentOn={{ table: 'people.farmers', id: farmer.id }} entityIds={[farmer.id, farmer.party_id]} />}

      {tab === 'consent' && (
        <>
          {/*
            * The position FIRST, then the records (owner, 2026-09-25: "which party what consent
            * is pending, issued and valid, expired? this shall be at respective place"). The
            * table below can only show consents that exist; a consent this farmer must have and
            * nobody ever issued has no row in it, and that is the one worth seeing.
            */}
          {farmer.party_id && (
            <ConsentPosition
              onIssue={() => navigate(`/consent-capture?party=${farmer.party_id}`)}
              partyId={farmer.party_id}
            />
          )}
          {/* Consent per plot (owner, 2026-09-27: "Consent table for farmer shall be arranged there
              from the plots"): every plot's consent, then the farmer's other consents below. */}
          {farmer.party_id && <PlotConsentList farmerPartyId={farmer.party_id} linkOnly />}
          <div className="section-heading"><h3>Other consents <span className="muted">({(consents.data ?? []).filter((row) => !row.plot_id).length})</span></h3></div>
          <QueryState empty="No consent has been issued for this farmer." query={consents}>
            <RecordTable onRowClick={setOpenConsent} columns={consentColumns} rows={(consents.data ?? []).filter((row) => !row.plot_id)} />
          </QueryState>
        </>
      )}
      {/* Every consent has its two-language document and both routes (owner, 2026-09-28). */}
      {openConsent && <PlotConsentDrawer consentId={openConsent.id} onClose={() => setOpenConsent(null)} />}
    </Drawer>
  );
}

const CHANNEL_NAME: Record<string, string> = { whatsapp: 'WhatsApp', sms: 'SMS', call: 'Phone call', email: 'Email' };

function channelsInOrder(farmer: FarmerRow): string {
  const order = farmer.channel_order
    ?? (farmer.communication_preference === 'none' ? [] : [farmer.communication_preference]);
  return order.length === 0 ? 'Offline - reached in person' : order.map((channel) => CHANNEL_NAME[channel] ?? channel).join(' → ');
}

/** The form's own words for the entity and referrer kinds, so the view says what was chosen. */
const ENTITY_TYPE_LABEL: Record<string, string> = {
  fpo: 'Farmer Producer Organisation (FPO)', cooperative: 'Cooperative', shg: 'Self-help group (SHG)', society: 'Society',
  trust: 'Trust', company: 'Company', other: 'Other',
};
const REFERRER_TYPE_LABEL: Record<string, string> = {
  farmer: 'Another farmer', lead_farmer: 'Lead farmer', field_staff: 'Field staff', stakeholder: 'Stakeholder',
  community_leader: 'Community leader', other: 'Other',
};
